import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiX, FiSearch, FiCamera, FiDroplet, FiTrendingUp,
  FiCalendar, FiClock, FiStar, FiCheck, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { searchFoods, addMealLog } from '../../services/database';
import { useMealLogs } from '../../hooks/useFirestore';
import { collection, query, where, getDocs, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './FoodLogger.css';

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', icon: '🌅', time: '7:00 AM' },
  { id: 'lunch', label: 'Lunch', icon: '🌞', time: '12:00 PM' },
  { id: 'dinner', label: 'Dinner', icon: '🌙', time: '6:00 PM' },
  { id: 'snack', label: 'Snacks', icon: '🍎', time: 'Anytime' }
];

const FoodLogger = ({ targetMacros, nutritionPlan }) => {
  const { currentUser } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMeal, setSelectedMeal] = useState('breakfast');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loggedFoods, setLoggedFoods] = useState({
    breakfast: [],
    lunch: [],
    dinner: [],
    snack: []
  });
  const [recentFoods, setRecentFoods] = useState([]);
  const [favoriteFoods, setFavoriteFoods] = useState([]);
  const [waterIntake, setWaterIntake] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [viewMode, setViewMode] = useState('daily'); // daily, weekly, monthly
  const [fetchedNutritionPlan, setFetchedNutritionPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Use provided targets, or fetch from nutrition plan, or use defaults
  const getTargets = () => {
    if (targetMacros) {
      return {
        calories: targetMacros.calories || 2000,
        protein: targetMacros.protein?.grams || 150,
        carbs: targetMacros.carbs?.grams || 200,
        fat: targetMacros.fat?.grams || 67,
        water: 8
      };
    }

    if (fetchedNutritionPlan?.calculations?.macros) {
      const macros = fetchedNutritionPlan.calculations.macros;
      return {
        calories: fetchedNutritionPlan.calculations.targetCalories || 2000,
        protein: macros.protein?.grams || 150,
        carbs: macros.carbs?.grams || 200,
        fat: macros.fat?.grams || 67,
        water: 8
      };
    }

    // Default values
    return {
      calories: 2000,
      protein: 150,
      carbs: 200,
      fat: 67,
      water: 8
    };
  };

  const targets = getTargets();

  // Auto-fetch nutrition plan if not provided via props
  useEffect(() => {
    const fetchNutritionPlan = async () => {
      if (targetMacros || nutritionPlan || !currentUser) return;

      try {
        setLoadingPlan(true);
        const plansRef = collection(db, 'nutritionPlans');
        const plansQuery = query(
          plansRef,
          where('clientId', '==', currentUser.uid),
          where('status', '==', 'active'),
          firestoreLimit(1)
        );
        const plansSnapshot = await getDocs(plansQuery);

        if (!plansSnapshot.empty) {
          const planData = { id: plansSnapshot.docs[0].id, ...plansSnapshot.docs[0].data() };
          setFetchedNutritionPlan(planData);
        }
      } catch (error) {
        console.error('Error fetching nutrition plan:', error);
      } finally {
        setLoadingPlan(false);
      }
    };

    fetchNutritionPlan();
  }, [currentUser, targetMacros, nutritionPlan]);

  // Fetch meal logs for selected date
  const startOfDay = new Date(selectedDate);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(selectedDate);
  endOfDay.setHours(23, 59, 59, 999);

  const { data: todaysMeals, loading: mealsLoading } = useMealLogs(
    currentUser?.uid,
    startOfDay,
    endOfDay
  );

  // Load logged foods from Firestore
  useEffect(() => {
    if (todaysMeals && todaysMeals.length > 0) {
      const organized = {
        breakfast: [],
        lunch: [],
        dinner: [],
        snack: []
      };

      todaysMeals.forEach(meal => {
        if (organized[meal.mealType]) {
          organized[meal.mealType] = meal.foods || [];
        }
      });

      setLoggedFoods(organized);
    }
  }, [todaysMeals]);

  // Search foods
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchFoods(query, 15);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching foods:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Add food to meal
  const addFoodToMeal = (food, mealType) => {
    const foodItem = {
      ...food,
      id: `${Date.now()}-${Math.random()}`,
      servings: 1,
      totalCalories: food.calories,
      totalProtein: food.protein,
      totalCarbs: food.carbs,
      totalFat: food.fat
    };

    setLoggedFoods(prev => ({
      ...prev,
      [mealType]: [...prev[mealType], foodItem]
    }));

    // Add to recent foods
    if (!recentFoods.find(f => f.id === food.id)) {
      setRecentFoods(prev => [food, ...prev.slice(0, 9)]);
    }

    setSearchQuery('');
    setSearchResults([]);
  };

  // Update servings
  const updateServings = (mealType, foodId, servings) => {
    setLoggedFoods(prev => ({
      ...prev,
      [mealType]: prev[mealType].map(food =>
        food.id === foodId
          ? {
              ...food,
              servings: parseFloat(servings) || 1,
              totalCalories: food.calories * (parseFloat(servings) || 1),
              totalProtein: food.protein * (parseFloat(servings) || 1),
              totalCarbs: food.carbs * (parseFloat(servings) || 1),
              totalFat: food.fat * (parseFloat(servings) || 1)
            }
          : food
      )
    }));
  };

  // Remove food
  const removeFood = (mealType, foodId) => {
    setLoggedFoods(prev => ({
      ...prev,
      [mealType]: prev[mealType].filter(food => food.id !== foodId)
    }));
  };

  // Toggle favorite
  const toggleFavorite = (food) => {
    const isFavorite = favoriteFoods.find(f => f.id === food.id);

    if (isFavorite) {
      setFavoriteFoods(prev => prev.filter(f => f.id !== food.id));
    } else {
      setFavoriteFoods(prev => [...prev, food]);
    }
  };

  // Save meal log
  const saveMealLog = async () => {
    try {
      // Save each meal type that has foods
      for (const [mealType, foods] of Object.entries(loggedFoods)) {
        if (foods.length > 0) {
          const totals = calculateMealTotals(mealType);

          await addMealLog({
            userId: currentUser.uid,
            trainerId: null, // Would get from user profile
            date: selectedDate,
            mealType,
            foods: foods.map(f => ({
              foodId: f.id,
              foodName: f.name,
              servings: f.servings,
              calories: f.totalCalories,
              protein: f.totalProtein,
              carbs: f.totalCarbs,
              fat: f.totalFat
            })),
            totalCalories: totals.calories,
            totalProtein: totals.protein,
            totalCarbs: totals.carbs,
            totalFat: totals.fat
          });
        }
      }

      alert('Meal log saved successfully!');
    } catch (error) {
      console.error('Error saving meal log:', error);
      alert('Failed to save meal log');
    }
  };

  // Calculate totals
  const calculateMealTotals = (mealType) => {
    const foods = loggedFoods[mealType] || [];
    return foods.reduce(
      (totals, food) => ({
        calories: totals.calories + (food.totalCalories || 0),
        protein: totals.protein + (food.totalProtein || 0),
        carbs: totals.carbs + (food.totalCarbs || 0),
        fat: totals.fat + (food.totalFat || 0)
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  const calculateDailyTotals = () => {
    return Object.keys(loggedFoods).reduce(
      (totals, mealType) => {
        const mealTotals = calculateMealTotals(mealType);
        return {
          calories: totals.calories + mealTotals.calories,
          protein: totals.protein + mealTotals.protein,
          carbs: totals.carbs + mealTotals.carbs,
          fat: totals.fat + mealTotals.fat
        };
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  const dailyTotals = calculateDailyTotals();

  // Water intake handlers
  const addWater = () => setWaterIntake(prev => Math.min(prev + 1, 16));
  const removeWater = () => setWaterIntake(prev => Math.max(prev - 1, 0));

  // Date navigation
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  // Barcode scanner (placeholder - would use device camera)
  const handleScanBarcode = () => {
    setShowScanner(true);
    // TODO: Implement barcode scanning with device camera
    // Could use libraries like quagga.js or html5-qrcode
    alert('Barcode scanning feature coming soon!');
    setShowScanner(false);
  };

  const activePlan = nutritionPlan || fetchedNutritionPlan;

  return (
    <div className="food-logger">
      {/* Nutrition Plan Banner */}
      {activePlan && (
        <div className="nutrition-plan-banner">
          <FiTrendingUp />
          <div className="plan-info">
            <span className="plan-label">Active Plan:</span>
            <strong>{activePlan.name || activePlan.goal || 'Custom Plan'}</strong>
            {activePlan.goal && <span className="plan-goal">• {activePlan.goal}</span>}
          </div>
          <span className="plan-badge">Active</span>
        </div>
      )}

      {/* Header */}
      <div className="logger-header">
        <div className="date-selector">
          <button onClick={() => changeDate(-1)} className="date-nav-btn">
            <FiChevronLeft />
          </button>

          <div className="current-date">
            <FiCalendar />
            <span>{selectedDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            })}</span>
          </div>

          <button onClick={() => changeDate(1)} className="date-nav-btn">
            <FiChevronRight />
          </button>
        </div>

        <div className="view-mode-toggle">
          {['daily', 'weekly', 'monthly'].map(mode => (
            <button
              key={mode}
              className={`mode-btn ${viewMode === mode ? 'active' : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Macro Progress Rings */}
      <div className="macro-progress">
        <MacroRing
          label="Calories"
          current={Math.round(dailyTotals.calories)}
          target={targets.calories}
          color="#ff6b35"
        />
        <MacroRing
          label="Protein"
          current={Math.round(dailyTotals.protein)}
          target={targets.protein}
          color="#4ecdc4"
          unit="g"
        />
        <MacroRing
          label="Carbs"
          current={Math.round(dailyTotals.carbs)}
          target={targets.carbs}
          color="#f4a261"
          unit="g"
        />
        <MacroRing
          label="Fat"
          current={Math.round(dailyTotals.fat)}
          target={targets.fat}
          color="#e76f51"
          unit="g"
        />
      </div>

      {/* Water Intake */}
      <div className="water-tracker">
        <div className="water-header">
          <FiDroplet />
          <span>Water Intake</span>
          <span className="water-count">{waterIntake} / {targets.water} glasses</span>
        </div>
        <div className="water-glasses">
          {Array.from({ length: targets.water }).map((_, i) => (
            <button
              key={i}
              className={`glass ${i < waterIntake ? 'filled' : ''}`}
              onClick={() => i < waterIntake ? removeWater() : addWater()}
            >
              <FiDroplet />
            </button>
          ))}
        </div>
      </div>

      {/* Food Search */}
      <div className="food-search">
        <div className="search-input-container">
          <FiSearch />
          <input
            type="text"
            placeholder="Search foods or scan barcode..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button className="scan-btn" onClick={handleScanBarcode}>
            <FiCamera />
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results-dropdown">
            {searchResults.map(food => (
              <button
                key={food.id}
                className="search-result-item"
                onClick={() => addFoodToMeal(food, selectedMeal)}
              >
                <div className="result-info">
                  <strong>{food.name}</strong>
                  {food.brand && <span className="brand">{food.brand}</span>}
                  <div className="result-macros">
                    {Math.round(food.calories)}cal •
                    P:{Math.round(food.protein)}g •
                    C:{Math.round(food.carbs)}g •
                    F:{Math.round(food.fat)}g
                  </div>
                </div>
                <FiPlus />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recent & Favorites Quick Add */}
      {!searchQuery && (recentFoods.length > 0 || favoriteFoods.length > 0) && (
        <div className="quick-add-section">
          {favoriteFoods.length > 0 && (
            <div className="quick-add-category">
              <h3>
                <FiStar /> Favorites
              </h3>
              <div className="quick-add-list">
                {favoriteFoods.slice(0, 5).map(food => (
                  <button
                    key={food.id}
                    className="quick-add-item"
                    onClick={() => addFoodToMeal(food, selectedMeal)}
                  >
                    <span>{food.name}</span>
                    <span className="quick-cals">{Math.round(food.calories)}cal</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {recentFoods.length > 0 && (
            <div className="quick-add-category">
              <h3>
                <FiClock /> Recent
              </h3>
              <div className="quick-add-list">
                {recentFoods.slice(0, 5).map(food => (
                  <button
                    key={food.id}
                    className="quick-add-item"
                    onClick={() => addFoodToMeal(food, selectedMeal)}
                  >
                    <span>{food.name}</span>
                    <span className="quick-cals">{Math.round(food.calories)}cal</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Meal Tabs */}
      <div className="meal-tabs">
        {MEAL_TYPES.map(meal => {
          const totals = calculateMealTotals(meal.id);

          return (
            <button
              key={meal.id}
              className={`meal-tab ${selectedMeal === meal.id ? 'active' : ''}`}
              onClick={() => setSelectedMeal(meal.id)}
            >
              <span className="meal-icon">{meal.icon}</span>
              <div className="meal-info">
                <span className="meal-label">{meal.label}</span>
                <span className="meal-time">{meal.time}</span>
                <span className="meal-calories">
                  {Math.round(totals.calories)}cal
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Logged Foods for Selected Meal */}
      <div className="logged-foods">
        {loggedFoods[selectedMeal].length === 0 ? (
          <div className="empty-meal-state">
            <FiPlus size={40} />
            <p>No foods logged for {selectedMeal}</p>
            <p className="hint">Search for foods above to add them</p>
          </div>
        ) : (
          <>
            {loggedFoods[selectedMeal].map(food => (
              <div key={food.id} className="logged-food-item">
                <div className="food-main">
                  <div className="food-name-section">
                    <strong>{food.name}</strong>
                    <button
                      className="favorite-btn"
                      onClick={() => toggleFavorite(food)}
                    >
                      <FiStar
                        className={favoriteFoods.find(f => f.id === food.id) ? 'filled' : ''}
                      />
                    </button>
                  </div>

                  <div className="food-macros-display">
                    <span>{Math.round(food.totalCalories)} cal</span>
                    <span>P: {Math.round(food.totalProtein)}g</span>
                    <span>C: {Math.round(food.totalCarbs)}g</span>
                    <span>F: {Math.round(food.totalFat)}g</span>
                  </div>
                </div>

                <div className="food-controls">
                  <div className="serving-control">
                    <button
                      onClick={() => updateServings(selectedMeal, food.id, food.servings - 0.5)}
                      className="serving-btn"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={food.servings}
                      onChange={(e) =>
                        updateServings(selectedMeal, food.id, e.target.value)
                      }
                      className="serving-input"
                    />
                    <button
                      onClick={() => updateServings(selectedMeal, food.id, food.servings + 0.5)}
                      className="serving-btn"
                    >
                      +
                    </button>
                  </div>

                  <button
                    className="delete-btn"
                    onClick={() => removeFood(selectedMeal, food.id)}
                  >
                    <FiX />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Save Button */}
      <button className="save-log-btn" onClick={saveMealLog}>
        <FiCheck />
        Save Today's Log
      </button>
    </div>
  );
};

// Macro Ring Component
const MacroRing = ({ label, current, target, color, unit = '' }) => {
  const percentage = Math.min((current / target) * 100, 100);
  const remaining = Math.max(target - current, 0);

  return (
    <div className="macro-ring">
      <svg viewBox="0 0 100 100" className="ring-svg">
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="8"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${percentage * 2.827} 282.7`}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="ring-content">
        <div className="ring-value">
          {current}
          {unit && <span className="unit">{unit}</span>}
        </div>
        <div className="ring-label">{label}</div>
        <div className="ring-remaining">{remaining}{unit} left</div>
      </div>
    </div>
  );
};

export default FoodLogger;
